<template>
  <q-dialog
    :model-value="modelValue"
    :persistent="persistent"
    data-test-id="add-circuits-dialog"
    @update:model-value="$emit('update:model-value', $event)"
  >
    <q-card
      class="relative-position"
      bordered
      flat
      style="min-width: 500px;"
    >
      <q-card-section>
        <div class="row items-center no-wrap">
          <div class="col-auto">
            <q-btn
              v-if="!persistent"
              v-close-popup
              icon="close"
              flat
              round
              dense
            />
          </div>
          <div class="col text-h6 q-pl-md">
            Manage Circuit Access for {{ user.firstName }} <span class="sensitive-data">{{ user.lastName }}</span>
          </div>
        </div>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div class="text-subtitle2 q-pl-md q-mb-md">
          Circuit Access Management
        </div>
        <div class="text-grey-9 q-pl-md q-mb-md">
          Select circuits to grant {{ user.firstName }} circuit admin access to. The user will have admin and employee access to all current and future sites within the selected circuits. At least one circuit must be selected.
        </div>

        <q-select
          ref="circuitSelect"
          v-model="selectedCircuits"
          class="q-mx-md"
          label="Circuit Access"
          :options="allCircuitOptions"
          map-options
          option-label="name"
          option-value="id"
          multiple
          use-chips
          stack-label
          bottom-slots
          empty-label="No circuits available"
          :rules="[val => val.length > 0 || 'Please select at least one circuit']"
        >
          <template #no-option>
            <q-item>
              <q-item-section class="text-grey">
                No circuits available for management.
              </q-item-section>
            </q-item>
          </template>
        </q-select>

        <div class="q-mt-md q-mx-md">
          <div class="text-subtitle2 q-mb-sm">
            Current Circuit Access
          </div>
          <div class="text-grey-9 q-mb-sm">
            {{ user.firstName }} currently has circuit admin access to:
          </div>
          <q-chip
            v-for="circuit in selectedUserCircuits"
            :key="circuit.id"
            dense
            :color="isCircuitSelected(circuit.id) ? 'primary' : 'grey-5'"
            :text-color="isCircuitSelected(circuit.id) ? 'white' : 'grey-8'"
          >
            {{ circuit.name }}
          </q-chip>
        </div>
      </q-card-section>

      <q-card-actions
        class="q-pb-md q-pr-md"
        align="right"
      >
        <q-btn
          v-close-popup
          flat
          :disable="isSaving"
        >
          Cancel
        </q-btn>
        <q-btn
          color="primary"
          :loading="isSaving"
          :disable="!hasChanges || !isValid"
          @click="updateAuthorizedCircuits"
        >
          Update Circuit Access
        </q-btn>
      </q-card-actions>
      <q-inner-loading :showing="isSaving" />
    </q-card>
  </q-dialog>
</template>

<script>
export default {
  name: 'AddCircuitsDialog',
  props: {
    user: {
      type: Object,
      required: true
    },
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    },
    persistent: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:model-value', 'save'],
  data () {
    return {
      selectedCircuits: [],
      isSaving: false
    }
  },
  context: ['currentUser'],
  configuration: ['currentCircuit'],
  computed: {
    allCircuitOptions () {
      return this.currentUser.authorizedCircuitOptions || []
    },
    selectedUserCircuits () {
      return this.user.authorizedCircuits?.data || []
    },
    hasChanges () {
      const originalCircuitIds = _.map(this.selectedUserCircuits, 'id')
      const currentCircuitIds = _.map(this.selectedCircuits, 'id')
      return !_.isEmpty(_.xor(originalCircuitIds, currentCircuitIds))
    },
    isValid () {
      return !_.isEmpty(this.selectedCircuits)
    }
  },
  watch: {
    modelValue (newValue) {
      if (newValue) {
        this.reset()
      }
    },
    selectedUserCircuits: {
      handler () {
        if (this.modelValue) {
          this.reset()
        }
      },
      deep: true
    }
  },
  methods: {
    close () {
      this.$emit('update:model-value', false)
    },
    reset () {
      this.selectedCircuits = _.map(this.selectedUserCircuits, circuit => ({
        id: circuit.id,
        name: circuit.name
      }))
      this.isSaving = false
    },
    isCircuitSelected (circuitId) {
      return _.some(this.selectedCircuits, circuit => circuit.id === circuitId)
    },
    async updateAuthorizedCircuits () {
      if (!this.hasChanges || !this.isValid) return

      if (!this.$refs.circuitSelect.validate()) return

      this.isSaving = true

      try {
        const circuitIds = _.map(this.selectedCircuits, 'id')
        const result = await this.user.updateAuthorizedCircuits({ circuitIds })

        if (result.data.success) {
          const originalCount = this.selectedUserCircuits.length
          const newCount = this.selectedCircuits.length
          const addedCount = newCount - originalCount

          let message = `Successfully updated circuit access for ${this.user.firstName}`
          if (addedCount > 0) {
            message += ` (added ${addedCount} circuit${addedCount > 1 ? 's' : ''})`
          } else if (addedCount < 0) {
            message += ` (removed ${Math.abs(addedCount)} circuit${Math.abs(addedCount) > 1 ? 's' : ''})`
          }

          this.$q.notify({
            type: 'positive',
            message
          })

          this.$emit('save')
          this.close()
        } else if (result.errors?.length) {
          this.$mh.showErrors(result.errors)
        }
      } catch (error) {
        console.error('Error updating circuits:', error)
        this.$q.notify({
          type: 'negative',
          message: 'Failed to update circuit access. ' + error.message
        })
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>
